import { yupResolver } from "@hookform/resolvers/yup";
import { CalendarAltIcon } from "assets/icons";
/* eslint-disable @typescript-eslint/no-explicit-any */
import cn from "classnames";
import { t } from "i18next";
import { Controller, useForm } from "react-hook-form";
import {
  Checkbox,
  DateInput,
  FormField,
  Input,
  RadioButton,
  Switch,
  TimeInput
} from "ui";

import { CouponType, CouponUsageScope } from "../../../constants";
import { CouponFormValues } from "../../../coupons.types";
import { couponValidationSchema } from "../../../helpers";
import {
  InfluencerCodeKindField,
  InfluencerField,
  RequirementField
} from "./atoms";
import { getCouponDescriptionByType } from "./CouponForm.helpers";
import { SharingCouponFormProps } from "./CouponForm.types";

import styles from "./CouponForm.module.scss";

const CouponForm = ({
  formId,
  defaultValues,
  couponType,
  onSubmit
}: SharingCouponFormProps) => {
  const { control, setValue, setError, watch, handleSubmit } =
    useForm<CouponFormValues>({
      defaultValues,
      resolver: couponValidationSchema[couponType]
        ? yupResolver(couponValidationSchema[couponType])
        : undefined
    });

  const fromDateWatch = watch("fromDate");
  const discountTypeWatch = watch("discountType");

  const couponDescription = getCouponDescriptionByType(couponType);

  const isUnlimitedWatch = watch("isUnlimited");

  return (
    <form id={formId} className={styles.form} onSubmit={handleSubmit(onSubmit)}>
      {couponDescription && <p>{t(couponDescription)}</p>}

      <Controller
        control={control}
        name="code"
        render={({ field: { value, onChange }, fieldState: { error } }) => (
          <Input
            value={value}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            errorMessage={error?.message}
            label={"Coupon Name"}
            placeholder={"Ex.: COUPON15"}
            maxLength={20}
          />
        )}
      />

      <div
        className={cn(styles.form, {
          [styles.form_twoColumns]: couponType === CouponType.SpendAndSaveCoupon
        })}
      >
        <RequirementField couponType={couponType} control={control} />

        <div className={styles.form_discountBlock}>
          {discountTypeWatch === "percentage" ? (
            <Controller
              control={control}
              name="discountPercentage"
              render={({
                field: { value, onChange },
                fieldState: { error }
              }) => (
                <Input
                  value={value || ""}
                  onChange={onChange}
                  errorMessage={error?.message}
                  label={`${t("Discount")} (%)`}
                  type="number"
                  onlyInteger
                  placeholder={t("Enter a discount")}
                  maxLength={2}
                />
              )}
            />
          ) : (
            <Controller
              control={control}
              name="discountAmount"
              render={({
                field: { value, onChange },
                fieldState: { error }
              }) => (
                <Input
                  value={value || ""}
                  onChange={onChange}
                  errorMessage={error?.message}
                  label={`${t("Discount")} (FCFA)`}
                  type="number"
                  onlyInteger
                  placeholder={t("Enter a discount")}
                  maxLength={10}
                />
              )}
            />
          )}

          <Controller
            control={control}
            name="discountType"
            render={({ field: { value, onChange } }) => (
              <div className={styles.form_radioGroup}>
                <RadioButton
                  name="discountType"
                  value="percentage"
                  label={t("Percentage")}
                  checked={value === "percentage"}
                  onChange={(e) => {
                    if (e.currentTarget.checked) {
                      onChange("percentage");
                      setValue("discountPercentage", null);
                      setValue("discountAmount", null);
                    }
                  }}
                />
                <RadioButton
                  name="discountType"
                  value="amount"
                  label={t("Fixed")}
                  checked={value === "amount"}
                  onChange={(e) => {
                    if (e.currentTarget.checked) {
                      onChange("amount");
                      setValue("discountPercentage", null);
                      setValue("discountAmount", null);
                    }
                  }}
                />
              </div>
            )}
          />
        </div>
      </div>

      {couponType === CouponType.InfluencerCoupon && (
        <>
          <InfluencerField control={control} />
          <InfluencerCodeKindField control={control} />
        </>
      )}

      {couponType === CouponType.CustomCoupon && (
        <Controller
          control={control}
          name="usageScope"
          render={({ field: { value, onChange } }) => (
            <FormField label="For All or One User">
              <div className={styles.form_switch}>
                <p
                  className={cn(styles.form_switchLabel, {
                    [styles.form_switchLabel__active]:
                      value === CouponUsageScope.EachUser
                  })}
                >
                  {t("All Users")}
                </p>
                <Switch
                  checked={value === CouponUsageScope.OneUser}
                  onChange={() =>
                    onChange(
                      value === CouponUsageScope.EachUser
                        ? CouponUsageScope.OneUser
                        : CouponUsageScope.EachUser
                    )
                  }
                />
                <p
                  className={cn(styles.form_switchLabel, {
                    [styles.form_switchLabel__active]:
                      value === CouponUsageScope.OneUser
                  })}
                >
                  {t("One User")}
                </p>
              </div>
            </FormField>
          )}
        />
      )}

      {couponType !== CouponType.SpendAndSaveCoupon && (
        <div className={styles.form_twoColumns}>
          <Controller
            control={control}
            name="fromDate"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <DateInput
                value={value}
                onChange={onChange}
                label={"From Date"}
                errorMessage={error?.message}
                minDate={new Date()}
                calendarIcon={<CalendarAltIcon />}
              />
            )}
          />

          <Controller
            control={control}
            name="fromTime"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TimeInput
                value={value}
                onChange={onChange}
                label="From time"
                format="HH:mm"
                errorMessage={error?.message}
              />
            )}
          />

          <Controller
            control={control}
            name="toDate"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <DateInput
                value={value}
                onChange={onChange}
                label={"To Date"}
                errorMessage={error?.message}
                minDate={fromDateWatch ? new Date(fromDateWatch) : undefined}
                disabled={isUnlimitedWatch}
                calendarIcon={<CalendarAltIcon />}
              />
            )}
          />

          <Controller
            control={control}
            name="toTime"
            render={({ field: { value, onChange }, fieldState: { error } }) => (
              <TimeInput
                value={value}
                onChange={onChange}
                label="To time"
                format="HH:mm"
                disabled={isUnlimitedWatch}
                errorMessage={error?.message}
              />
            )}
          />

          {(couponType === CouponType.CustomCoupon ||
            couponType === CouponType.InfluencerCoupon) && (
            <Controller
              control={control}
              name="isUnlimited"
              render={({ field: { value, onChange } }) => (
                <Checkbox
                  checked={value}
                  onChange={() => {
                    onChange(!value);
                    if (!value) {
                      setValue("toDate", null);
                      setError("toDate", {
                        message: ""
                      });
                    }
                  }}
                  label={t("Unlimited")}
                />
              )}
            />
          )}
        </div>
      )}
    </form>
  );
};

export default CouponForm;
